import { useState } from "react";

export default function GisSettingsPanel() {
  const [settings, setSettings] = useState({
    defaultSrid: "31982",
    coordSystem: "SIRGAS 2000 / UTM zone 22S",
    exportFormat: "GeoJSON",
    decimalPrecision: "6",
    geometryTolerance: "0.001",
    allowInvalidGeom: false,
    simplifyDefault: "0.0001",
    basemap: "osm",
  });

  const handleChange = (field: string, value: string | boolean) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const basemaps = [
    { id: "osm", name: "OpenStreetMap", icon: "ri-road-map-line" },
    { id: "satellite", name: "Satélite", icon: "ri-radar-line" },
    { id: "ortho", name: "Ortofoto municipal", icon: "ri-image-line" },
    { id: "light", name: "Mapa claro", icon: "ri-sun-line" },
    { id: "dark", name: "Mapa escuro", icon: "ri-moon-line" },
    { id: "topo", name: "Topográfico", icon: "ri-landscape-line" },
  ];

  const layers = [
    "Árvores CTA", "Áreas verdes", "UCs", "APPs", "Bairros",
    "Logradouros", "Lotes/quadras", "Fotos georreferenciadas",
    "Risco crítico", "Manejo recomendado",
  ];

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Configurações GIS</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">SRID padrão</label>
            <select value={settings.defaultSrid} onChange={(e) => handleChange("defaultSrid", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 outline-none">
              <option value="31982">31982 - SIRGAS 2000 / UTM 22S</option>
              <option value="31983">31983 - SIRGAS 2000 / UTM 23S</option>
              <option value="4326">4326 - WGS 84</option>
              <option value="4674">4674 - SIRGAS 2000</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Sistema de coordenadas</label>
            <input type="text" value={settings.coordSystem} onChange={(e) => handleChange("coordSystem", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Formato de exportação padrão</label>
            <select value={settings.exportFormat} onChange={(e) => handleChange("exportFormat", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 outline-none">
              <option value="GeoJSON">GeoJSON</option>
              <option value="Shapefile">Shapefile (SHP)</option>
              <option value="KML">KML/KMZ</option>
              <option value="CSV">CSV com coordenadas</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Precisão decimal de coordenadas</label>
            <input type="number" value={settings.decimalPrecision} onChange={(e) => handleChange("decimalPrecision", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Tolerância de validação geométrica (m)</label>
            <input type="number" step="0.001" value={settings.geometryTolerance} onChange={(e) => handleChange("geometryTolerance", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Simplificação de geometria padrão</label>
            <input type="number" step="0.0001" value={settings.simplifyDefault} onChange={(e) => handleChange("simplifyDefault", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-accent-200 focus:border-accent-500 outline-none" />
          </div>
        </div>
        <div className="mt-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={settings.allowInvalidGeom} onChange={(e) => handleChange("allowInvalidGeom", e.target.checked)} className="w-4 h-4 rounded border-background-300 text-accent-500 focus:ring-accent-400" />
            <span className="text-sm text-foreground-700">Permitir geometrias inválidas</span>
          </label>
        </div>
      </div>

      {/* Basemaps */}
      <div className="bg-white rounded-xl border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Basemap padrão</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {basemaps.map((b) => (
            <button
              key={b.id}
              onClick={() => handleChange("basemap", b.id)}
              className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-colors ${settings.basemap === b.id ? "border-accent-500 bg-accent-50" : "border-background-200 bg-white hover:border-background-300"}`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings.basemap === b.id ? "bg-accent-100 text-accent-600" : "bg-foreground-100 text-foreground-500"}`}>
                <i className={`${b.icon} text-lg`} />
              </div>
              <span className="text-xs text-foreground-700 text-center">{b.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Default layers */}
      <div className="bg-white rounded-xl border border-background-200/70 p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-4">Camadas padrão</h3>
        <div className="flex flex-wrap gap-2">
          {layers.map((l) => (
            <span key={l} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-50 text-accent-700 text-xs font-medium border border-accent-200">
              <i className="ri-checkbox-circle-line text-sm" />{l}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}